import { useCallback } from 'react';
import { createFileRoute } from '@tanstack/react-router';

import type {
  AdminUser,
  Department,
  DepartmentsArchiveSort,
  SortDirection,
  UsersArchiveSort,
} from '~/api/types';
import { ArchiveUsersDepartmentsScreen } from '~/features/archive/departments-users/ArchiveUsersDepartmentsScreen';
import {
  ARCHIVE_TABS,
  type ArchiveTabsValue,
} from '~/features/archive/departments-users/constants';
import { UsersDepartmentsArchiveSearchParamsProvider } from '~/features/archive/departments-users/params';
import type {
  ArchivedUsersDepartmentsSearchParams,
  ArchivedUsersDepartmentsSearchParamsHandler,
} from '~/features/archive/departments-users/types';
import { USER_ROLES } from '~/features/auth/roles';
import { useAuthState } from '~/features/auth/useAuthState';
import { Route as CustomerAdminDepartmentDetailsRoute } from '~/routes/customer-administration/departments.$departmentId';
import { Route as CustomerAdminUserDetailsRoute } from '~/routes/customer-administration/users.profile.$userId';
import { Route as DepartmentDetailsRoute } from '~/routes/user-administration/departments.$departmentId';
import { Route as UserDetailsRoute } from '~/routes/user-administration/users.profile.$userId';
import { protectedRoute } from '~/utils/routesAuthorization';

export const Route = createFileRoute('/user-administration/archive')({
  component: RouteComponent,
  validateSearch: (search): ArchivedUsersDepartmentsSearchParams => {
    // Validate and parse the search params into Roua typed state
    const defaultSortDir: SortDirection =
      search.sortDir === 'asc' ? 'asc' : 'desc';
    const sortDir = search.sortDir ? defaultSortDir : undefined;

    const possibleTabValues = Object.values(ARCHIVE_TABS);
    const searchTab = Number(search.tab) as ArchiveTabsValue;
    const tab = possibleTabValues.includes(searchTab) ? searchTab : undefined;

    return {
      page: search.page ? Number(search.page) || 1 : undefined,
      sort:
        (search.sort as
          | UsersArchiveSort
          | DepartmentsArchiveSort
          | undefined) ?? undefined,
      search:
        search.search && typeof search.search === 'string'
          ? decodeURI(search.search)
          : undefined,
      sortDir,
      tab,
    };
  },
  beforeLoad: () => {
    protectedRoute({
      roles: [
        USER_ROLES.AdminAbbott,
        USER_ROLES.HcpAdmin,
        USER_ROLES.NonHcpAdmin,
      ],
    });
  },
});

function RouteComponent() {
  const navigate = Route.useNavigate();

  const { userInfo } = useAuthState();
  const userRole = userInfo?.role;

  const onSearchParamsChange: ArchivedUsersDepartmentsSearchParamsHandler =
    useCallback((onSearch) => navigate({ search: onSearch }), [navigate]);

  const handleDepartmentRowPress = useCallback(
    (department: Department) => {
      void navigate({
        to:
          userRole === USER_ROLES.HcpAdmin
            ? DepartmentDetailsRoute.fullPath
            : CustomerAdminDepartmentDetailsRoute.fullPath,
        params: { departmentId: department.id },
      });
    },
    [navigate, userRole],
  );

  const handleUserRowPress = useCallback(
    (user: AdminUser) => {
      void navigate({
        to:
          userRole === USER_ROLES.HcpAdmin
            ? UserDetailsRoute.fullPath
            : CustomerAdminUserDetailsRoute.fullPath,
        params: { userId: user.id },
      });
    },
    [navigate, userRole],
  );

  return (
    <UsersDepartmentsArchiveSearchParamsProvider
      params={Route.useSearch()}
      onSearchParamsChange={onSearchParamsChange}
    >
      <ArchiveUsersDepartmentsScreen
        onDepartmentRowPress={handleDepartmentRowPress}
        onUserRowPress={handleUserRowPress}
      />
    </UsersDepartmentsArchiveSearchParamsProvider>
  );
}
